import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { signedDocumentStatusText } from "../../edit/docs/signatures/SignedDocumentStatusTag";
import { FencyProvider } from "@fencyai/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaExclamationTriangle, FaRedo, FaSearch, FaSignature, FaTimes } from "react-icons/fa";
import { FencyContentStatus, ContractDto } from "../../../../autogen/bff-api";
import { MemoryChat } from "../../../../common/documents/MemoryChat";
import { isAiAnalysable } from "../../../../common/documents/CombinedDocuments";
import { isSignedDocumentDto, isUnsignedSignedDocument } from "../../../../common/documents/utils";
import { fencyPromise } from "../../../../common/fency/fencySetup";
import { useFency } from "../../../../common/fency/useFency";
import {
  DocumentSyncStatus,
  useFencyBulkDocumentSync,
} from "../../../../common/fency/useFencyBulkDocumentSync";
import { ExplainedIcon } from "../../../../common/support/ExplainedIcon/ExplainedIcon";
const StatusIcon = ({ item, onRetry }: { item: DocumentSyncStatus; onRetry: () => void }) => {
  const { t } = useTranslation();
  if (isSignedDocumentDto(item.doc) && isUnsignedSignedDocument(item.doc)) {
    return (
      <ExplainedIcon
        explanation={t("{{status}} – can't be searched", { status: signedDocumentStatusText(item.doc.status) })}
        icon={FaSignature}
        color="smMuted"
      />
    );
  }
  if (item.syncing) {
    return <Spinner size="sm" />;
  }
  if (item.error || item.status === FencyContentStatus.SynchronizationError) {
    return (
      <Flex align="center" gap="1">
        <ExplainedIcon explanation={t("Couldn't be prepared for search")} icon={FaExclamationTriangle} color="smError" />
        <Tooltip label={t("Try again")}>
          <IconButton
            aria-label={t("Try again") ?? ""}
            icon={<Box as={FaRedo} boxSize="3" />}
            size="xs"
            variant="ghost"
            onClick={onRetry}
          />
        </Tooltip>
      </Flex>
    );
  }
  if (item.status === FencyContentStatus.Synchronized) {
    return <Box as={FaCheckCircle} color="smSuccess" boxSize="4" />;
  }
  return <Spinner size="sm" />;
};

const isStillSyncing = (item: DocumentSyncStatus) =>
  !isUnsignedSignedDocument(item.doc) &&
  (item.syncing ||
    (item.status !== FencyContentStatus.Synchronized &&
      item.status !== FencyContentStatus.SynchronizationError &&
      !item.error));

export const FencyChatWithContract = ({
  contract,
  refetchContract,
}: {
  contract: ContractDto;
  refetchContract: () => void | Promise<void>;
}) => {
  const { t } = useTranslation();
  const { fetchCreateStreamClientToken, fetchCreateBulkFencySessionClientToken } = useFency();

  const documentFields = contract.documentFields;
  const fencyDocs = useMemo(() => {
    if (!documentFields) return [];
    const docs = [
      ...documentFields.documents,
      ...documentFields.signedDocuments,
    ] as (typeof documentFields.documents[0] | typeof documentFields.signedDocuments[0])[];
    return docs.filter(isAiAnalysable);
  }, [documentFields]);

  const { documentStatuses, retry } = useFencyBulkDocumentSync(fencyDocs, refetchContract);

  const [documentFilter, setDocumentFilter] = useState("");
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    documentStatuses.forEach((item) => {
      if (item.status === FencyContentStatus.Synchronized) {
        initial.add(item.doc.id);
      }
    });
    return initial;
  });

  useEffect(() => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      documentStatuses.forEach((item) => {
        if (item.status === FencyContentStatus.Synchronized && !next.has(item.doc.id)) {
          next.add(item.doc.id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [documentStatuses]);

  const toggleDocSelection = useCallback((docId: string, checked: boolean) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(docId);
      else next.delete(docId);
      return next;
    });
  }, []);

  const syncedDocIds = useMemo(
    () =>
      documentStatuses
        .filter((item) => item.status === FencyContentStatus.Synchronized)
        .map((item) => item.doc.id),
    [documentStatuses]
  );

  const selectAll = useCallback(() => {
    setSelectedDocIds(new Set(syncedDocIds));
  }, [syncedDocIds]);

  const deselectAll = useCallback(() => {
    setSelectedDocIds(new Set());
  }, []);

  const filteredDocumentStatuses = useMemo(() => {
    const q = documentFilter.trim().toLowerCase();
    if (!q) return documentStatuses;
    return documentStatuses.filter((item) =>
      item.doc.name.toLowerCase().includes(q)
    );
  }, [documentStatuses, documentFilter]);

  const { documentIds, signedDocumentIds, selectedCount, canSend, sendDisabledHint } = useMemo(() => {
    const docIds: string[] = [];
    const signedIds: string[] = [];
    let syncedCount = 0;
    let syncingCount = 0;
    documentStatuses.forEach((item) => {
      if (isStillSyncing(item)) syncingCount++;
      if (item.status === FencyContentStatus.Synchronized) {
        syncedCount++;
        if (selectedDocIds.has(item.doc.id)) {
          if (isSignedDocumentDto(item.doc)) {
            signedIds.push(item.doc.id);
          } else {
            docIds.push(item.doc.id);
          }
        }
      }
    });
    const hasSelected = docIds.length > 0 || signedIds.length > 0;
    const hint = hasSelected
      ? undefined
      : syncedCount > 0
        ? t("Select at least one document to search")
        : syncingCount > 0
          ? t("No documents ready yet")
          : t("None of these documents can be searched yet");
    return {
      documentIds: docIds,
      signedDocumentIds: signedIds,
      selectedCount: docIds.length + signedIds.length,
      canSend: hasSelected,
      sendDisabledHint: hint,
    };
  }, [documentStatuses, selectedDocIds, t]);

  const fetchToken = useCallback(async () => {
    return fetchCreateBulkFencySessionClientToken(documentIds, signedDocumentIds);
  }, [
    fetchCreateBulkFencySessionClientToken,
    documentIds,
    signedDocumentIds,
  ]);

  if (!documentFields) {
    return (
      <Flex p="6" justifyContent="center" alignItems="center">
        <Text color="smMuted">{t("No documents available for AI chat.")}</Text>
      </Flex>
    );
  }

  if (fencyDocs.length === 0) {
    return (
      <Flex p="6" justifyContent="center" alignItems="center">
        <Text color="smMuted">
          {t("No documents available for AI chat. Only PDF documents can be searched.")}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      flex={1}
      minH={0}
      height="100%"
      overflow={{ base: "auto", md: "hidden" }}
      gap="4"
      direction={{ base: "column", md: "row" }}
      data-testid="contract-ai-chat-layout"
    >
      <Flex
        flex={1}
        flexDirection="column"
        minW={0}
        minH={{ base: "420px", md: 0 }}
        overflow="hidden"
      >
        <FencyProvider
          fency={fencyPromise}
          fetchCreateStreamClientToken={fetchCreateStreamClientToken}
        >
          <MemoryChat
            fetchClientToken={fetchToken}
            documentName={t("contract documents")}
            emptyStateText={t("Ask something about these documents.")}
            placeholderText={t("Ask something about these documents…")}
            canSend={canSend}
            sendDisabledHint={sendDisabledHint}
          />
        </FencyProvider>
      </Flex>

      <Box
        width={{ base: "100%", md: "240px" }}
        maxH={{ base: "40vh", md: "none" }}
        order={{ base: -1, md: 0 }}
        flexShrink={0}
        borderLeftWidth={{ base: 0, md: "1px" }}
        borderBottomWidth={{ base: "1px", md: 0 }}
        borderColor="smBorder"
        p="4"
        overflowY="auto"
        data-testid="contract-ai-chat-documents"
      >
        <Text fontWeight="semibold" mb="2" fontSize="sm">
          {t("Documents to search")}
        </Text>
        <InputGroup size="sm" mb="2">
          <InputLeftElement pointerEvents="none">
            <Box as={FaSearch} color="smTextDisabled" boxSize="3" />
          </InputLeftElement>
          <Input
            placeholder={t("Filter documents…") ?? ""}
            value={documentFilter}
            onChange={(e) => setDocumentFilter(e.target.value)}
          />
          {documentFilter && (
            <InputRightElement>
              <IconButton
                aria-label={t("Clear search") ?? ""}
                icon={<Box as={FaTimes} boxSize="3" />}
                size="xs"
                variant="ghost"
                onClick={() => setDocumentFilter("")}
              />
            </InputRightElement>
          )}
        </InputGroup>
        <ButtonGroup size="xs" mb="2" isAttached>
          <Button
            onClick={selectAll}
            isDisabled={syncedDocIds.length === 0}
            variant="outline"
          >
            {t("Select all")}
          </Button>
          <Button
            onClick={deselectAll}
            isDisabled={selectedDocIds.size === 0}
            variant="outline"
          >
            {t("Deselect all")}
          </Button>
        </ButtonGroup>
        <Text fontSize="xs" color="smMuted" mb="3">
          {t("Searching in {{count}} documents", { count: selectedCount })}
        </Text>
        <VStack align="stretch" spacing="2">
          {documentFilter.trim() && filteredDocumentStatuses.length === 0 && (
            <Text fontSize="sm" color="smMuted">
              {t("No documents match your search")}
            </Text>
          )}
          {filteredDocumentStatuses.map((item) => {
            const isSynchronized = item.status === FencyContentStatus.Synchronized;
            const isChecked = selectedDocIds.has(item.doc.id);
            return (
              <Flex
                key={item.doc.id}
                align="center"
                gap="2"
                fontSize="sm"
                opacity={isSynchronized ? 1 : 0.7}
              >
                <Checkbox
                  isChecked={isChecked}
                  isDisabled={!isSynchronized}
                  onChange={(e) => toggleDocSelection(item.doc.id, e.target.checked)}
                  size="sm"
                />
                <Box
                  flex={1}
                  minW={0}
                  noOfLines={2}
                  title={item.doc.name}
                  cursor={isSynchronized ? "pointer" : "default"}
                  onClick={
                    isSynchronized
                      ? () => toggleDocSelection(item.doc.id, !isChecked)
                      : undefined
                  }
                >
                  {item.doc.name}
                </Box>
                <StatusIcon item={item} onRetry={() => retry(item.doc.id)} />
              </Flex>
            );
          })}
        </VStack>
      </Box>
    </Flex>
  );
};
